import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import type { AcceptedFinding, Audit, Issue, ReadabilityMetric, ReadabilityReport } from '@audit/shared-types';

type AuditWithIssues = Audit & {
  issues: Issue[];
  acceptedFindings: AcceptedFinding[];
  suppressedIssueCount: number;
  matchedAcceptedFindingIds: number[];
  page: { url: string; site: { name: string } };
};

const SEVERITY_COLOUR: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  serious: 'bg-orange-100 text-orange-700',
  moderate: 'bg-yellow-100 text-yellow-700',
  minor: 'bg-gray-100 text-gray-600',
};

function parseFailureSummary(summary?: string): string[] {
  if (!summary) {
    return [];
  }

  return summary
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => line.replace(/^Fix (any|all) of the following:\s*/i, '').trim())
    .filter(Boolean);
}

function formatReadabilityMetricValue(metric?: ReadabilityMetric): string {
  if (!metric || metric.value === null || metric.value === undefined || metric.value === '') {
    return 'Unavailable';
  }

  return metric.unit ? `${metric.value} ${metric.unit}` : String(metric.value);
}

function ReadabilitySection({ report }: { report: ReadabilityReport | null | undefined }) {
  if (!report) {
    return null;
  }

  const headlineTone =
    report.status === 'failed'
      ? 'bg-red-50 border-red-200 text-red-800'
      : report.status === 'disabled'
        ? 'bg-gray-50 border-gray-200 text-gray-700'
        : report.findings.length > 0
          ? 'bg-amber-50 border-amber-200 text-amber-800'
          : 'bg-green-50 border-green-200 text-green-800';

  const keyMetrics = [
    {
      key: 'flesch_reading_ease',
      label: 'Flesch Reading Ease',
      description: 'Scores how easy the text is to read. Higher scores mean simpler writing and easier comprehension.',
      metric: report.metrics['flesch_reading_ease'],
    },
    {
      key: 'flesch_kincaid_grade',
      label: 'Flesch-Kincaid Grade',
      description: 'Estimates the U.S. school grade level needed to understand the text. Lower values are easier.',
      metric: report.metrics['flesch_kincaid_grade'],
    },
    {
      key: 'reading_time',
      label: 'Reading Time',
      description: 'Estimated time for an average reader to read the analysed content from start to finish.',
      metric: report.metrics['reading_time'],
    },
    {
      key: 'sentence_count',
      label: 'Sentence Count',
      description: 'Total number of sentences detected in the analysed content.',
      metric: report.metrics['sentence_count'],
    },
    {
      key: 'lexicon_count',
      label: 'Word Count',
      description: 'Total number of words in the analysed content.',
      metric: report.metrics['lexicon_count'],
    },
    {
      key: 'syllable_count',
      label: 'Syllable Count',
      description: 'Total number of syllables across all words, used by multiple readability formulas.',
      metric: report.metrics['syllable_count'],
    },
  ] as const;

  return (
    <section className="mb-8">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Readability</h3>
          <p className="text-sm text-gray-500 mt-1">
            Language: <span className="text-gray-700">{report.language}</span>
            {' '} &bull; Source: <span className="text-gray-700">{report.extraction.source}</span>
            {' '} &bull; {report.extraction.textLength} characters analysed
            {report.extraction.truncated && <> &bull; truncated before analysis</>}
          </p>
        </div>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${headlineTone}`}>
          {report.status}
        </span>
      </div>

      <div className={`rounded-xl border p-4 ${headlineTone}`}>
        {report.status === 'failed' ? (
          <p className="text-sm">{report.errorMessage ?? 'Readability analysis failed.'}</p>
        ) : report.status === 'disabled' ? (
          <p className="text-sm">Readability analysis is disabled in the current environment.</p>
        ) : report.findings.length > 0 ? (
          <div className="space-y-2">
            {report.findings.map((finding) => (
              <div key={finding.id} className="rounded-lg border border-amber-200 bg-white/70 p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${SEVERITY_COLOUR[finding.severity] ?? 'bg-gray-100 text-gray-600'}`}>
                    {finding.severity}
                  </span>
                  <span className="text-sm font-medium text-gray-900">{finding.label}</span>
                </div>
                <p className="text-sm text-gray-700">{finding.message}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm">Readability thresholds passed for this page.</p>
        )}
      </div>

      <div className="grid gap-4 mt-4 md:grid-cols-2 xl:grid-cols-3">
        {keyMetrics.map(({ key, label, description, metric }) => (
          <div key={key} className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 flex items-center gap-1.5">
              <span>{label}</span>
              <span
                className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-gray-300 text-[10px] font-bold text-gray-500 cursor-help"
                title={description}
                aria-label={`${label}: ${description}`}
              >
                i
              </span>
            </p>
            <p className="mt-2 text-lg font-semibold text-gray-900">{formatReadabilityMetricValue(metric)}</p>
            {metric?.note && <p className="mt-2 text-xs text-gray-500">{metric.note}</p>}
          </div>
        ))}
      </div>

      {report.extraction.excerpt && (
        <details className="mt-4">
          <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-900">
            View analysed text excerpt
          </summary>
          <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 whitespace-pre-wrap">
            {report.extraction.excerpt}
          </div>
        </details>
      )}
    </section>
  );
}

function formatTarget(target?: string[]): string {
  if (!target || target.length === 0) {
    return 'Selector unavailable';
  }

  return target.join(' ');
}

function decodeHtmlAttribute(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}

function extractImagePreview(node: Issue['nodes'][number]): { src: string; alt?: string; source: 'img' | 'background-image' } | null {
  if (node.previewImageUrl) {
    return {
      src: node.previewImageUrl,
      ...(node.previewLabel !== undefined ? { alt: node.previewLabel } : {}),
      source: node.previewSource ?? 'img',
    };
  }

  const html = node.html;
  if (!html) {
    return null;
  }

  const imgMatch = html.match(/<img\b[^>]*\ssrc\s*=\s*(["'])(.*?)\1/i);
  if (imgMatch?.[2]) {
    const altMatch = html.match(/<img\b[^>]*\salt\s*=\s*(["'])(.*?)\1/i);

    return {
      src: decodeHtmlAttribute(imgMatch[2]),
      ...(altMatch?.[2] ? { alt: decodeHtmlAttribute(altMatch[2]) } : {}),
      source: 'img',
    };
  }

  const backgroundImageMatch = html.match(/background-image\s*:\s*url\((['"]?)(.*?)\1\)/i);
  if (!backgroundImageMatch?.[2]) {
    return null;
  }

  const ariaLabelMatch = html.match(/\saria-label\s*=\s*(["'])(.*?)\1/i);

  return {
    src: decodeHtmlAttribute(backgroundImageMatch[2]),
    ...(ariaLabelMatch ? { alt: decodeHtmlAttribute(ariaLabelMatch[2] ?? '') } : {}),
    source: 'background-image',
  };
}

function AffectedNodeCard({
  node,
  ruleId,
  index,
}: {
  node: Issue['nodes'][number];
  ruleId: string;
  index: number;
}) {
  const failureReasons = parseFailureSummary(node.failureSummary);
  const imagePreview = extractImagePreview(node);

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
          Affected Element {index + 1}
        </p>
        <p className="text-xs text-gray-500 text-right">Page location</p>
      </div>

      <p className="text-xs font-mono text-gray-700 break-all">{formatTarget(node.target)}</p>

      {failureReasons.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-700">What failed</p>
          <ul className="mt-1 space-y-1 text-xs text-gray-700 list-disc list-inside">
            {failureReasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
        </div>
      )}

      {imagePreview && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-700">Image</p>
          <img
            src={imagePreview.src}
            alt={imagePreview.alt ?? 'Image from the affected element'}
            className="max-h-72 w-auto rounded border border-gray-200 bg-white"
          />
          <p className="text-xs text-gray-500 break-all">{imagePreview.src}</p>
          <p className="text-xs text-gray-600">
            {imagePreview.source === 'img' ? 'Alt text' : 'Accessible name'}: {imagePreview.alt?.trim() ? imagePreview.alt : 'Missing or empty'}
          </p>
        </div>
      )}

      {!imagePreview && node.screenshotDataUrl && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-700">Screenshot</p>
          <img
            src={node.screenshotDataUrl}
            alt={`Screenshot of the affected element for ${ruleId}`}
            className="max-h-72 w-auto rounded border border-gray-200 bg-white"
          />
        </div>
      )}

      {node.html && (
        <details>
          <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-900">
            View affected HTML
          </summary>
          <pre className="mt-2 p-3 bg-gray-900 text-gray-100 text-xs rounded-lg overflow-auto whitespace-pre-wrap">
            {node.html}
          </pre>
        </details>
      )}
    </div>
  );
}

function extractLooseJsonField(raw: string, fieldName: string): string {
  const startPattern = new RegExp(`"${fieldName}"\\s*:\\s*"`, 'i');
  const startMatch = startPattern.exec(raw);

  if (!startMatch || startMatch.index < 0) {
    return '';
  }

  const startIndex = startMatch.index + startMatch[0].length;
  const subsequentFields = ['problem', 'impact', 'explanation', 'fixSuggestion']
    .filter((candidate) => candidate !== fieldName)
    .map((candidate) => raw.indexOf(`"${candidate}"`, startIndex))
    .filter((index) => index >= 0);

  const endCandidates = [
    ...subsequentFields,
    raw.lastIndexOf('"}'),
    raw.length,
  ].filter((index) => index >= startIndex);

  const endIndex = Math.min(...endCandidates);
  return raw.slice(startIndex, endIndex).replace(/[",\s]+$/, '').trim();
}

function parseAiExplanation(explanation?: string | null): { problem?: string; impact?: string; fixSuggestion?: string } {
  if (!explanation) {
    return {};
  }

  const lines = explanation.split('\n').map((line) => line.trim()).filter(Boolean);
  const problemLine = lines.find((line) => line.startsWith('Problem:'));
  const impactLine = lines.find((line) => line.startsWith('Impact:'));

  const result: { problem?: string; impact?: string; fixSuggestion?: string } = {};

  if (problemLine) {
    result.problem = problemLine.replace(/^Problem:\s*/, '');
  }

  if (impactLine) {
    result.impact = impactLine.replace(/^Impact:\s*/, '');
  }

  if (!result.problem && !result.impact && explanation.trim().startsWith('{')) {
    const problem = extractLooseJsonField(explanation, 'problem');
    const impact = extractLooseJsonField(explanation, 'impact');
    const fixSuggestion = extractLooseJsonField(explanation, 'fixSuggestion');

    if (problem) result.problem = problem;
    if (impact) result.impact = impact;
    if (fixSuggestion) result.fixSuggestion = fixSuggestion;
  }

  return result;
}

function looksLikeCodeSnippet(value: string): boolean {
  return /<[^>]+>|^[.#]?[a-z0-9_-]+\s*\{|;\s*$|\b(?:aria-|alt=|href=|class=|style=)/im.test(value);
}

export default function AuditResultPage() {
  const { auditId } = useParams<{ auditId: string }>();
  const queryClient = useQueryClient();
  const [summary, setSummary] = useState<string | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const acceptFindingMutation = useMutation({
    mutationFn: async (issueId: number) => {
      if (!auditId) throw new Error('auditId is required');
      return apiClient.post(`/audits/${auditId}/issues/${issueId}/accept`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['audit', auditId] });
    },
  });

  const restoreFindingMutation = useMutation({
    mutationFn: async (acceptedFindingId: number) => {
      if (!auditId) throw new Error('auditId is required');
      return apiClient.delete(`/audits/${auditId}/accepted-findings/${acceptedFindingId}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['audit', auditId] });
    },
  });

  const { data: audit, isLoading } = useQuery({
    queryKey: ['audit', auditId],
    queryFn: () =>
      apiClient.get<AuditWithIssues>(`/audits/${auditId ?? ''}`).then((r) => r.data),
    enabled: !!auditId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'running' ? 3000 : false;
    },
  });

  async function generateSummary() {
    if (!audit) return;
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const res = await apiClient.post<{ summary: string }>('/ai/summary', {
        auditId: audit.id,
      });
      setSummary(res.data.summary);
    } catch {
      setSummaryError('AI unavailable — please try again.');
    } finally {
      setSummaryLoading(false);
    }
  }

  if (isLoading) return <p className="text-sm text-gray-500">Loading audit…</p>;
  if (!audit) return <p className="text-sm text-red-600">Audit not found</p>;

  const readabilityFindingCount = audit.readabilityReport?.findings.length ?? 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Audit #{audit.id}</h2>
          <p className="text-sm text-gray-500 mt-1">
            Status: <strong>{audit.status}</strong> &bull; {audit.issueCount} active {audit.issueCount === 1 ? 'finding' : 'findings'}
            {audit.suppressedIssueCount > 0 && (
              <> &bull; {audit.suppressedIssueCount} accepted {audit.suppressedIssueCount === 1 ? 'finding' : 'findings'} suppressed</>
            )}
            {audit.page && (
              <> &bull; <span className="text-gray-400">{audit.page.url}</span></>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {audit.status === 'complete' && (
            <button
              onClick={() => void generateSummary()}
              disabled={summaryLoading}
              className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {summaryLoading ? 'Generating…' : '✨ AI Summary'}
            </button>
          )}
          <a
            href={`/api/reports/${audit.id}/export/pdf`}
            className="text-sm border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-50"
          >
            Export PDF
          </a>
          <a
            href={`/api/reports/${audit.id}/export/csv`}
            className="text-sm border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg hover:bg-gray-50"
          >
            Export CSV
          </a>
        </div>
      </div>

      {/* AI Summary panel */}
      {summaryError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          {summaryError}
        </div>
      )}
      {summary && (
        <div className="mb-6 p-5 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
            ✨ AI Summary
          </p>
          <p className="text-sm text-blue-900">{summary}</p>
        </div>
      )}

      {/* Summary counts */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        {(['critical', 'serious', 'moderate', 'minor'] as const).map((s) => (
          <div key={s} className="bg-white border border-gray-200 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{audit[`${s}Count`]}</p>
            <p className="text-xs text-gray-500 capitalize mt-1">{s}</p>
          </div>
        ))}
      </div>

      <ReadabilitySection report={audit.readabilityReport} />

      {/* Issues list */}
      {audit.status === 'pending' || audit.status === 'running' ? (
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
          Audit in progress…
        </div>
      ) : (
        <div className="space-y-3">
          {audit.issues.map((issue) => (
            <IssueCard
              key={issue.id}
              issue={issue}
              pageUrl={audit.page?.url ?? ''}
              onAccept={async () => acceptFindingMutation.mutateAsync(issue.id)}
              accepting={acceptFindingMutation.isPending && acceptFindingMutation.variables === issue.id}
            />
          ))}
          {audit.issues.length === 0 && audit.status === 'complete' && (
            <div className={`rounded-xl p-6 text-center border ${audit.suppressedIssueCount > 0 || readabilityFindingCount > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
              <p className={`${audit.suppressedIssueCount > 0 || readabilityFindingCount > 0 ? 'text-amber-800' : 'text-green-700'} font-semibold`}>
                {audit.suppressedIssueCount > 0
                  ? 'No accessibility findings remain for this audit.'
                  : 'No accessibility issues found'}
              </p>
              {audit.suppressedIssueCount > 0 ? (
                <p className="text-sm text-amber-700 mt-2">
                  {audit.suppressedIssueCount} accepted {audit.suppressedIssueCount === 1 ? 'finding was' : 'findings were'} suppressed for this page.
                </p>
              ) : readabilityFindingCount > 0 ? (
                <p className="text-sm text-amber-700 mt-2">
                  Accessibility passed, but {readabilityFindingCount} readability {readabilityFindingCount === 1 ? 'finding is' : 'findings are'} listed above.
                </p>
              ) : (
                <p className="text-sm text-green-700 mt-2">Everything in this scan passed.</p>
              )}
            </div>
          )}
        </div>
      )}

      {audit.acceptedFindings.length > 0 && (
        <section className="mt-8">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Accepted Findings</h3>
              <p className="text-sm text-gray-500 mt-1">
                Accepted findings are hidden from active results on this page until restored.
              </p>
            </div>
            <p className="text-xs text-gray-500">{audit.acceptedFindings.length} accepted</p>
          </div>

          <div className="space-y-3">
            {audit.acceptedFindings.map((finding) => (
              <AcceptedFindingCard
                key={finding.id}
                finding={finding}
                matchedInCurrentAudit={audit.matchedAcceptedFindingIds.includes(finding.id)}
                restoring={restoreFindingMutation.isPending && restoreFindingMutation.variables === finding.id}
                onRestore={async () => restoreFindingMutation.mutateAsync(finding.id)}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function IssueCard({
  issue,
  pageUrl,
  onAccept,
  accepting,
}: {
  issue: Issue;
  pageUrl: string;
  onAccept: () => Promise<unknown>;
  accepting: boolean;
}) {
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [aiResult, setAiResult] = useState<{
    problem?: string;
    impact?: string;
    explanation: string;
    fixSuggestion: string;
  } | null>(
    issue.aiExplanation
      ? { explanation: issue.aiExplanation, fixSuggestion: issue.aiFixSuggestion ?? '' }
      : null
  );

  const explanation = aiResult?.explanation ?? issue.aiExplanation;
  const structuredExplanation = parseAiExplanation(explanation);
  const fixSuggestion = aiResult?.fixSuggestion ?? issue.aiFixSuggestion ?? structuredExplanation.fixSuggestion;
  const hasStructuredExplanation = Boolean(
    structuredExplanation.problem || structuredExplanation.impact
  );
  const primaryNode = issue.nodes[0];
  const failureReasons = parseFailureSummary(primaryNode?.failureSummary);
  const affectedElementLabel =
    issue.nodes.length === 1 ? '1 affected element' : `${issue.nodes.length} affected elements`;

  async function getAiHelp() {
    setAiLoading(true);
    setAiError(null);
    try {
      const res = await apiClient.post<{
        problem?: string;
        impact?: string;
        explanation: string;
        fixSuggestion: string;
      }>(
        '/ai/fix-suggestion',
        {
          ruleId: issue.ruleId,
          description: issue.description,
          help: issue.help,
          nodeHtml: issue.nodes[0]?.html ?? undefined,
          pageUrl,
          issueId: issue.id,
        }
      );
      setAiResult(res.data);
    } catch {
      setAiError('AI unavailable — please try again.');
    } finally {
      setAiLoading(false);
    }
  }

  async function acceptFinding() {
    setAcceptError(null);
    try {
      await onAccept();
    } catch {
      setAcceptError('Could not accept this finding. Please try again.');
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`text-xs font-semibold px-2 py-0.5 rounded ${
                SEVERITY_COLOUR[issue.severity] ?? 'bg-gray-100 text-gray-600'
              }`}
            >
              {issue.severity}
            </span>
            <code className="text-xs text-gray-500 font-mono">{issue.ruleId}</code>
            <span className="text-xs text-gray-400">{issue.nodes.length} affected {issue.nodes.length === 1 ? 'element' : 'elements'}</span>
          </div>
          <p className="text-sm font-medium text-gray-900">{issue.help}</p>
          <p className="text-xs text-gray-500 mt-0.5">{issue.description}</p>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => void acceptFinding()}
            disabled={accepting}
            className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-1 rounded-lg hover:bg-amber-100 disabled:opacity-50 whitespace-nowrap"
          >
            {accepting ? 'Accepting…' : 'Accept finding'}
          </button>
          <button
            onClick={() => void getAiHelp()}
            disabled={aiLoading}
            className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-lg hover:bg-blue-100 disabled:opacity-50 whitespace-nowrap"
          >
            {aiLoading ? 'Thinking…' : explanation ? 'Regenerate AI' : 'Ask AI'}
          </button>
          <a
            href={issue.helpUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-600 hover:underline whitespace-nowrap"
          >
            Learn more
          </a>
        </div>
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">What Failed</p>
          {failureReasons.length > 0 ? (
            <ul className="mt-2 space-y-1 text-xs text-gray-700 list-disc list-inside">
              {failureReasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-xs text-gray-600">No element-level failure summary was provided for this issue.</p>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Affected Elements</p>
          <p className="mt-2 text-sm font-medium text-gray-900">{affectedElementLabel}</p>
          <p className="mt-2 text-xs text-gray-500">
            Review the element cards below for the exact page location, preview, and HTML for each occurrence.
          </p>
        </div>
      </div>

      <div className="mt-3 space-y-3">
        {issue.nodes.map((node, index) => (
          <AffectedNodeCard key={`${issue.id}-${formatTarget(node.target)}-${index}`} node={node} ruleId={issue.ruleId} index={index} />
        ))}
      </div>

      {aiError && <p className="mt-2 text-xs text-red-600">{aiError}</p>}
  {acceptError && <p className="mt-2 text-xs text-red-600">{acceptError}</p>}

      {explanation && (
        <div className="mt-3 p-3 bg-blue-50 rounded-lg text-xs text-blue-800">
          <p className="font-semibold mb-2">AI Explanation</p>
          {hasStructuredExplanation ? (
            <div className="space-y-2 leading-5">
              {structuredExplanation.problem && (
                <div>
                  <p className="font-semibold text-blue-900">Problem</p>
                  <p>{structuredExplanation.problem}</p>
                </div>
              )}
              {structuredExplanation.impact && (
                <div>
                  <p className="font-semibold text-blue-900">Impact</p>
                  <p>{structuredExplanation.impact}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="whitespace-pre-wrap leading-5">{explanation}</p>
          )}
        </div>
      )}

      {fixSuggestion && (
        <details className="mt-2">
          <summary className="text-xs text-blue-600 cursor-pointer hover:text-blue-800">
            View suggested fix
          </summary>
          {looksLikeCodeSnippet(fixSuggestion) ? (
            <pre className="mt-2 p-3 bg-gray-900 text-green-400 text-xs rounded-lg overflow-auto whitespace-pre-wrap">
              {fixSuggestion}
            </pre>
          ) : (
            <div className="mt-2 p-3 bg-blue-50 text-blue-900 text-xs rounded-lg whitespace-pre-wrap leading-5">
              {fixSuggestion}
            </div>
          )}
        </details>
      )}
    </div>
  );
}

function AcceptedFindingCard({
  finding,
  matchedInCurrentAudit,
  restoring,
  onRestore,
}: {
  finding: AcceptedFinding;
  matchedInCurrentAudit: boolean;
  restoring: boolean;
  onRestore: () => Promise<unknown>;
}) {
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const failureReasons = parseFailureSummary(finding.failureSummary);

  async function restoreFinding() {
    setRestoreError(null);
    try {
      await onRestore();
    } catch {
      setRestoreError('Could not restore this finding. Please try again.');
    }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span
              className={`text-xs font-semibold px-2 py-0.5 rounded ${
                SEVERITY_COLOUR[finding.severity] ?? 'bg-gray-100 text-gray-600'
              }`}
            >
              {finding.severity}
            </span>
            <code className="text-xs text-gray-500 font-mono">{finding.ruleId}</code>
            {matchedInCurrentAudit && (
              <span className="text-xs font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                Seen in this audit
              </span>
            )}
          </div>
          <p className="text-sm font-medium text-gray-900">{finding.help}</p>
          <p className="text-xs text-gray-500 mt-0.5">Accepted {formatAcceptedDate(finding.createdAt)}</p>
        </div>

        <button
          onClick={() => void restoreFinding()}
          disabled={restoring}
          className="text-xs border border-gray-300 text-gray-700 px-2.5 py-1 rounded-lg hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
        >
          {restoring ? 'Restoring…' : 'Restore'}
        </button>
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">Target</p>
          <p className="mt-2 text-xs font-mono text-gray-700 break-all">{formatTarget(finding.target)}</p>
          <p className="mt-2 text-xs text-gray-500">
            Match count: {finding.matchCount}
            {finding.lastMatchedAt && <> &bull; Last seen {formatAcceptedDate(finding.lastMatchedAt)}</>}
          </p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">What Failed</p>
          {failureReasons.length > 0 ? (
            <ul className="mt-2 space-y-1 text-xs text-gray-700 list-disc list-inside">
              {failureReasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-xs text-gray-600">No element-level failure summary was provided for this finding.</p>
          )}
        </div>
      </div>

      <details className="mt-3">
        <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-900">
          View stored HTML
        </summary>
        <pre className="mt-2 p-3 bg-gray-900 text-gray-100 text-xs rounded-lg overflow-auto whitespace-pre-wrap">
          {finding.html}
        </pre>
      </details>

      {restoreError && <p className="mt-2 text-xs text-red-600">{restoreError}</p>}
    </div>
  );
}

function formatAcceptedDate(value: string) {
  return new Date(value).toLocaleString();
}

